import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'react-toastify';
import ErrorBoundary from '@/components/common/ErrorBoundary';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const MISSING_ASSIGNMENT_TOAST_ID = 'missing-region-team-warning';
  
  // Medir a altura do header para compensar quando ele se torna fixo
  useEffect(() => {
    if (headerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          setHeaderHeight(entry.contentRect.height);
        }
      });
      
      resizeObserver.observe(headerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // After login: warn if user is not associated to a region and/or a team
  useEffect(() => {
    // Only run when authenticated area is mounted and user object is available
    if (!user) return;

    const hasRegion = user.region_id !== null && user.region_id !== undefined;
    // active_team_id can be optional on the type; guard using any to be tolerant to backend variations
    const activeTeamId = (user as unknown as { active_team_id?: number | null }).active_team_id;
    const hasTeam = activeTeamId !== null && activeTeamId !== undefined;

    const needsAttention = !hasRegion || !hasTeam;

    if (needsAttention) {
      toast.warning(
        (
          <span>
            Sua conta ainda não está associada a uma região e/ou equipe. Entre no seu perfil{' '}
            <Link to="/profile" style={{ textDecoration: 'underline' }}>
              (/profile)
            </Link>{' '}
            e altere os dados ou associe uma região e a uma equipe.
          </span>
        ),
        {
          toastId: MISSING_ASSIGNMENT_TOAST_ID,
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        }
      );
    } else {
      // Dismiss the warning if it was previously shown and the condition is resolved
      toast.dismiss(MISSING_ASSIGNMENT_TOAST_ID);
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div ref={headerRef}>
          <Header onMenuClick={() => setSidebarOpen(true)} />
        </div>
        
        {/* Espaço para compensar o header fixo */}
        <div id="header-spacer" className="header-spacer" style={{ height: `${headerHeight}px`, display: 'none' }}></div>

        {/* Page content */}
        <main className="flex-1 py-3 sm:py-4 content-area">
          <div className="mx-auto max-w-none px-0 sm:px-2 lg:px-4">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
